<template>
  <div
    class="card banner mb-3"
    :style="bannerStyle"
  >
    <div class="card-body">
      <div class="row row-block">
        <div class="col-md-4 p-5" :class="logoClass">
          <div
            v-if="organisation.logo"
            class="d-flex justify-content-center"
          >
            <img
              :src="organisation.logo"
              alt=""
              class="d-block img-logo img-fluid"
            />
          </div>

          <v-button
            v-else
            icon="edit"
            style-type="secondary"
            :block="true"
            text="phrase.add_logo"
            size="sm"
            :disabled="!organisation.isVerified"
            @click="focusLogoInput"
            :loader="logoLoader"
          />

          <input
            style="visibility: hidden; position: absolute"
            type="file"
            id="organisation-update-logo"
            ref="logoInput"
            accept="image/png, image/gif, image/jpeg"
            @change="updateLogo()"
          />
        </div>

        <div class="col-md-8 d-flex flex-column justify-content-center col-block">
          <h4 class="font-weight-bold mb-0 text-white">
            {{ organisation.name }}
          </h4>
          <div class="text-white text-small">
            <span v-if="organisation.address"><small>{{ organisation.address }}</small></span>
            <span v-if="organisation.zip"><small> | {{ organisation.zip }}</small></span>
            <span v-if="organisation.city">
              <small> | {{ organisation.city }}</small>
            </span>
          </div>

          <div class="mb-4 text-white">
            {{ organisation.description }}
          </div>
          <div>
            <div
              class="badge badge-pill badge-info mr-2"
              v-if="commercialsCount >= 0"
            >
              {{ commercialsCount }}
              {{ $tc('word.commercial', commercialsCount).toLowerCase() }}
            </div>

            <div
              class="badge badge-pill badge-info mr-2"
              v-if="reviewsCount >= 0"
            >
              {{ reviewsCount }}
              {{ $tc('word.review', reviewsCount).toLowerCase() }}
            </div>

            <a
              v-if="organisation.website && organisation.has_paid_license"
              class="btn btn-link text-white"
              :href="organisation.website"
              target="_blank"
            >
              <i class="fa fa-external-link-alt mr-2"/>
              {{ $t('phrase.view_website') }}
            </a>
          </div>
        </div>
      </div>

      <div
        class="d-none d-md-flex justify-content-end position-absolute"
        style="top: 5px; right: 5px"
      >
        <div class="mr-3">
          <v-button
            id="organisation-edit-logo"
            icon="image"
            style-type="secondary"
            text="phrase.edit_logo"
            size="sm"
            :disabled="!organisation.isVerified"
            @click="focusLogoInput"
            :loader="logoLoader"
          />
        </div>

        <div class="mr-3">
          <v-button
            id="organisation-edit-background"
            icon="camera"
            style-type="secondary"
            size="sm"
            text="phrase.edit_background"
            :disabled="!organisation.isVerified"
            @click="focusBannerInput"
            :loader="bannerLoader"
          />
        </div>

        <div>
          <v-button
            id="organisation-edit-information"
            icon="edit"
            style-type="secondary"
            size="sm"
            text="phrase.edit_information"
            @click="$emit('edit-information')"
            :disabled="!organisation.isVerified"
          />
        </div>
      </div>

      <div
        class="d-flex d-md-none justify-content-end position-absolute"
        style="top: 5px; right: 5px"
      >
        <v-dropdown-menu
          icon="ellipsis-h"
          style-type="secondary"
          size="sm"
        >
          <template v-slot:menu>
            <button
              id="organisation-edit-logo-sm"
              class="dropdown-item"
              type="button"
              @click="focusLogoInput"
              :disabled="!organisation.isVerified"
            >
              <i class="fa fa-image mr-2"></i> {{ $t('phrase.edit_logo') }}
            </button>
            <button
              id="organisation-edit-background-sm"
              class="dropdown-item"
              type="button"
              @click="focusBannerInput"
              :disabled="!organisation.isVerified"
            >
              <i class="fa fa-camera mr-2"></i> {{ $t('phrase.edit_background') }}
            </button>
            <button
              id="organisation-edit-information-sm"
              class="dropdown-item"
              type="button"
              @click="$emit('edit-information')"
              :disabled="!organisation.isVerified"
            >
              <i class="fa fa-edit mr-2"></i> {{ $t('phrase.edit_information') }}
            </button>
          </template>
        </v-dropdown-menu>
      </div>

      <v-form-errors
        :errors="errors"
      />
    </div>
    <div class="btn-edit-background position-absolute w-100">
      <input
        style="visibility: hidden; position: absolute"
        type="file"
        id="organisation-update-banner"
        ref="bannerInput"
        accept="image/png, image/gif, image/jpeg"
        @change="updateBanner()"
      />
    </div>
  </div>
</template>

<script>
import { OrganisationMixin } from '@/common/utils/mixins';
import api from '@/common/api';

import Button from '@/common/components/Button.vue';
import FormErrors from '@/common/components/FormErrors.vue';
import DropdownMenu from '@/common/components/DropdownMenu.vue';

export default {
  name: 'OrganisationBanner',
  components: {
    'v-button': Button,
    'v-form-errors': FormErrors,
    'v-dropdown-menu': DropdownMenu,
  },
  computed: {
    logoClass() {
      return ['d-flex', 'flex-column', 'justify-content-center', 'align-items-center', 'col-block'];
    },
  },
  data() {
    return {
      logoLoader: { loading: false },
      bannerLoader: { loading: false },
      errors: [],
    };
  },
  props: {
    commercialsCount: {
      type: Number,
      default() {
        return 0;
      },
    },
    organisation: {
      required: true,
      type: Object,
    },
    reviewsCount: {
      type: Number,
      default() {
        return 0;
      },
    },
  },
  methods: {
    makeFormData(key, file) {
      const formData = new FormData();
      formData.append(key, file);
      return formData;
    },
    async updateLogo() {
      this.logoLoader.loading = true;
      const response = await api.patch(
        `organisations/${this.organisation.slug}/logo/`,
        this.makeFormData('logo', this.$refs.logoInput.files[0]),
        { 'Content-Type': 'multipart/form-data' },
      );

      if (response.status === 400) {
        this.logoLoader.loading = false;
        this.errors = response.data.logo;
        return;
      }

      this.logoLoader.loading = false;
      this.$store.commit('updateOrg', response.data);
    },
    async updateBanner() {
      this.bannerLoader.loading = true;
      const response = await api.patch(
        `organisations/${this.organisation.slug}/banner/`,
        this.makeFormData('banner', this.$refs.bannerInput.files[0]),
        { 'Content-Type': 'multipart/form-data' },
      );
      if (response.status === 400) {
        this.bannerLoader.loading = false;
        this.errors = response.data.banner;
        return;
      }
      this.bannerLoader.loading = false;
      this.$store.commit('updateOrg', response.data);
    },
    focusLogoInput() {
      this.$refs.logoInput.click();
    },
    focusBannerInput() {
      this.$refs.bannerInput.click();
    },
  },
  mixins: [
    OrganisationMixin,
  ],
};
</script>

<style scoped>
.row-block {
  min-height: 134px;
}

.img-logo {
  max-height: 120px;
}

@media (max-width: 767px) {
  /* Small screens */
  .img-logo {
    width: 70%;
    height: auto;
    max-height: 999px;
  }
}
</style>
